import React, { useEffect, useState } from 'react';
import { supabase } from '../../supabase';
import { useEmpresaId } from '../../context/EmpresaContext';
import { formatCurrencyCRC, formatNumber, getCurrentWeekRangeIso } from '../../utils/erpLocale';

type ResumenState = {
  totalAplicaciones: number;
  costo: number;
  participacion: number;
  conEvidencia: number;
};

type AplicacionRow = {
  costo_bodega?: number | string | null;
  foto_timestamp?: string | null;
  foto_url?: string | null;
  fecha_aplicacion?: string | null;
  created_at?: string | null;
};

function isMissingTableError(error: any) {
  const code = String(error?.code || '').toUpperCase();
  const msg = String(error?.message || '').toLowerCase();
  return code === '42P01' || code === 'PGRST205' || msg.includes('could not find the table');
}

async function fetchResumenRows(empresaId: string | number, startIso: string, endIso: string) {
  const run = async (table: 'app_aplicaciones_registradas' | 'app_boletas_aplicaciones') =>
    supabase
      .from(table)
      .select('costo_bodega, foto_timestamp, foto_url, fecha_aplicacion, created_at')
      .eq('empresa_id', empresaId)
      .gte('fecha_aplicacion', startIso)
      .lt('fecha_aplicacion', endIso);

  const primary = await run('app_aplicaciones_registradas');
  if (!primary.error) return { data: ((primary.data || []) as unknown) as AplicacionRow[] };
  if (!isMissingTableError(primary.error)) return { data: [] as AplicacionRow[], error: primary.error };

  const fallback = await run('app_boletas_aplicaciones');
  if (fallback.error) {
    if (isMissingTableError(fallback.error)) return { data: [] as AplicacionRow[] };
    return { data: [] as AplicacionRow[], error: fallback.error };
  }
  return { data: ((fallback.data || []) as unknown) as AplicacionRow[] };
}

export function ResumenSemanal() {
  const empresaId = useEmpresaId();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resumen, setResumen] = useState<ResumenState>({
    totalAplicaciones: 0,
    costo: 0,
    participacion: 0,
    conEvidencia: 0,
  });

  useEffect(() => {
    const loadResumen = async () => {
      if (!empresaId) {
        setError('Empresa no definida para cargar resumen.');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const { startIso, endIso } = getCurrentWeekRangeIso();

        const { data, error: queryError } = await fetchResumenRows(empresaId, startIso, endIso);

        if (queryError) throw queryError;

        const rows = data || [];
        const totalAplicaciones = rows.length;
        const costo = rows.reduce((acc, item) => acc + Number(item.costo_bodega || 0), 0);
        const conEvidencia = rows.filter((item) => Boolean(item.foto_timestamp || item.foto_url)).length;
        const participacion = totalAplicaciones > 0 ? (conEvidencia / totalAplicaciones) * 100 : 0;

        setResumen({
          totalAplicaciones,
          costo,
          participacion,
          conEvidencia,
        });
      } catch (err: any) {
        setError(err?.message || 'No fue posible cargar el resumen semanal.');
      } finally {
        setLoading(false);
      }
    };

    loadResumen();
  }, [empresaId]);

  if (loading) {
    return <div className="p-6">Cargando...</div>;
  }

  return (
    <div className="p-6">
      <h1 className="text-3xl font-bold mb-4" style={{ color: 'var(--ink)' }}>Resumen Semanal</h1>

      {error && (
        <div className="mt-2 mb-4 rounded-lg border px-3 py-2 text-xs" style={{ borderColor: '#7f1d1d', background: 'rgba(127,29,29,0.18)', color: '#fecaca' }}>
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
        <div className="p-4 rounded-lg border" style={{ background: 'var(--surface-raised)', borderColor: 'var(--line)' }}>
          <p className="text-sm" style={{ color: 'var(--ink-faint)' }}>Total Aplicaciones</p>
          <p className="text-2xl font-bold mt-2" style={{ color: 'var(--ink)' }}>
            {formatNumber(resumen.totalAplicaciones)}
          </p>
        </div>
        <div className="p-4 rounded-lg border" style={{ background: 'var(--surface-raised)', borderColor: 'var(--line)' }}>
          <p className="text-sm" style={{ color: 'var(--ink-faint)' }}>Costo</p>
          <p className="text-2xl font-bold mt-2" style={{ color: 'var(--emp-accent)' }}>
            {formatCurrencyCRC(resumen.costo)}
          </p>
        </div>
        <div className="p-4 rounded-lg border" style={{ background: 'var(--surface-raised)', borderColor: 'var(--line)' }}>
          <p className="text-sm" style={{ color: 'var(--ink-faint)' }}>Cobertura Fotografica</p>
          <p className="text-2xl font-bold mt-2" style={{ color: '#38bdf8' }}>
            {formatNumber(resumen.participacion, 1)}%
          </p>
        </div>
        <div className="p-4 rounded-lg border" style={{ background: 'var(--surface-raised)', borderColor: 'var(--line)' }}>
          <p className="text-sm" style={{ color: 'var(--ink-faint)' }}>Aplicaciones con evidencia</p>
          <p className="text-2xl font-bold mt-2" style={{ color: 'var(--ink)' }}>{formatNumber(resumen.conEvidencia)}</p>
        </div>
      </div>
    </div>
  );
}

export default ResumenSemanal;
